import type { PricingToolState, StoredBiz } from "./pricingToolSlice";
import { MCC_NAMES, TEMPLATE_OPTIONS } from "./pricingData";
import { resolvePeriod } from "./periodMonths";
import { eur, monthLong, shortDate } from "./components/shared/formatters";

// ── Types ──

// The inputs a saved scenario puts back: the filters that chose the volume, the
// template that groups it and — unlike the filter blob — the prices and partner
// cuts that were typed. A reload drops those two because a reload is not an
// edit; naming a scenario and asking for it back is, so here they are the whole
// point of keeping.
//
// Everything the page persists for itself is here apart from `bizById`, which
// is a search cache rather than a choice. A filter added to one list and not
// the other would load from the page's own blob instead of from the scenario,
// so the slice's own test holds the two in step.
export const SCENARIO_INPUT_KEYS = [
  "channel",
  "source",
  "template",
  "country",
  "bizId",
  "selectedBiz",
  "areaCentre",
  "areaBizId",
  "areaBizCity",
  "areaAddress",
  "city",
  "radiusKm",
  "industryGroup",
  "mcc",
  "partnerId",
  "partnerName",
  "cohort",
  "minMonthlyVolume",
  "manualTPV",
  "manualTxns",
  "manualVol",
  "mixBasis",
  "manualBuckets",
  "periodStart",
  "periodEnd",
  "selMonth",
  "netBasis",
  "priceOverrides",
  "markupOverrides",
] as const;

export type ScenarioInputKey = (typeof SCENARIO_INPUT_KEYS)[number];

export type ScenarioInputs = Pick<PricingToolState, ScenarioInputKey> & {
  // Only the merchants the inputs above name. An area centred on a business
  // reads its coordinates out of this cache, so a snapshot without them would
  // restore to the city fallback — a different area under the same caption.
  bizById: Record<string, StoredBiz>;
};

// What the panel said at the moment of saving. Stored rather than recomputed on
// read: the volume behind it is a server response that a later fetch can
// restate, and a comparison whose "before" moves on its own is not one.
export interface ScenarioMetrics {
  tpv: number;
  txns: number;
  monthlyNet: number;
  annualNet: number;
}

export interface SavedScenario {
  id: string;
  name: string;
  savedAt: string;
  inputs: ScenarioInputs;
  metrics: ScenarioMetrics;
}

// Enough to hold a working session's worth of alternatives without letting an
// unattended tab grow the blob without bound. The oldest falls off the end.
export const MAX_SAVED_SCENARIOS = 20;

// ── Snapshot & restore ──

export function scenarioInputs(state: PricingToolState): ScenarioInputs {
  const bizById: Record<string, StoredBiz> = {};
  [state.bizId, state.areaBizId].forEach((id) => {
    if (id && state.bizById[id]) bizById[id] = state.bizById[id];
  });
  return clone({ ...pickInputs(state), bizById });
}

// The input fields, lifted off whichever of the two shapes carries them. Each
// key reads and writes the same field, which the compiler cannot follow through
// a loop over the key list.
function pickInputs(
  source: Pick<PricingToolState, ScenarioInputKey>
): Pick<PricingToolState, ScenarioInputKey> {
  const out = {} as Record<ScenarioInputKey, unknown>;
  SCENARIO_INPUT_KEYS.forEach((k) => {
    out[k] = source[k];
  });
  return out as Pick<PricingToolState, ScenarioInputKey>;
}

// Put the tool back the way the scenario found it.
//
// Cloned in both directions, so a snapshot is a value and never a view of the
// state it came from. Immer's structural sharing would keep a shared reference
// intact on its own, but only until the first non-reducer writes it — and after
// a refresh the snapshot is plain parsed JSON anyway, so copying makes the
// in-memory path behave as the reloaded one does rather than almost like it.
export function applyScenarioInputs(
  state: PricingToolState,
  inputs: ScenarioInputs
): void {
  const restored = clone(inputs);
  // Only the fields the snapshot actually carries. One written before a filter
  // existed has nothing to say about it, and writing that absence in as
  // `undefined` would leave the slice holding a value its own type rules out.
  Object.assign(
    state,
    Object.fromEntries(
      Object.entries(pickInputs(restored)).filter(([, v]) => v !== undefined)
    ) as Partial<Pick<PricingToolState, ScenarioInputKey>>
  );
  // Merged rather than replaced: the snapshot only carries the merchants its
  // own filters name, and the rest of the cache is still what search filled it
  // with.
  Object.assign(state.bizById, restored.bizById);
}

function clone<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

// Which merchants, over which months, the figures were fetched for. Mirrors the
// key the fetch itself is debounced on, so restoring a scenario can drop what
// was fetched exactly when a fetch is coming and not one time more: the common
// case is the same merchant under another template, and blanking that response
// would empty the panel to refetch what it already holds.
//
// The fields the tool has gained most recently are read through a fallback. It
// gains and loses area filters at a fair rate, so a stored snapshot that
// predates the next one is the expected case rather than a hypothetical; a
// filter nobody has heard of must read as absent and not take the page down.
export function cohortKey(i: ScenarioInputs): string {
  const period = [i.periodStart ?? "", i.periodEnd ?? ""];
  const centre =
    i.areaCentre === "business"
      ? (i.areaBizId ?? "")
      : i.areaCentre === "address"
        ? `${i.areaAddress?.lat ?? ""},${i.areaAddress?.lng ?? ""}`
        : i.city;
  if (i.source === "manual")
    return ["manual", i.manualTPV, i.manualTxns].join("|");
  if (i.source === "business")
    return ["business", i.channel, i.bizId ?? "", ...period].join("|");
  return [
    "area",
    i.channel,
    i.country,
    i.areaCentre,
    centre,
    i.radiusKm,
    i.industryGroup,
    i.mcc ?? "",
    i.partnerId ?? "",
    i.cohort ?? "",
    i.minMonthlyVolume ?? 0,
    ...period,
  ].join("|");
}

// What the modelled volume is drawn from. Two scenarios whose keys match are
// priced against the same money on the same basis, so the gap between their
// nets is the gap between their prices — which is the only reason to save one.
// Across different keys that gap is mostly the change of cohort, and reporting
// it as a pricing delta would put a number on screen that nothing supports.
//
// A typed mix is deliberately not part of this. It changes how the volume is
// split across the groups, not how much of it there is, and splitting it
// differently is a modelling choice of the same kind as pricing it differently
// — which is what a saved scenario is for holding.
export function volumeBasisKey(i: ScenarioInputs): string {
  return [
    cohortKey(i),
    // One month out of the period is a different month's volume, even though
    // it is the same response being read.
    i.selMonth ?? "",
    // The two nets answer different questions — what the merchant pays Klearly,
    // and what Klearly keeps once the partners are paid — so one is never the
    // baseline for the other however identical the cohort.
    i.netBasis,
  ].join("|");
}

// The chips that say what a scenario was modelled on, in the order the net-take
// panel captions the live one.
export function scenarioCaption(i: ScenarioInputs): string[] {
  const template =
    TEMPLATE_OPTIONS.find((t) => t.key === i.template)?.label ?? i.template;
  const period = resolvePeriod(i.periodStart, i.periodEnd);
  const scope =
    i.source === "business"
      ? (i.selectedBiz?.displayName ?? "No business")
      : i.source === "area"
        ? [
            i.areaCentre === "business"
              ? i.areaBizCity
              : (i.areaAddress?.label ?? i.city),
            `${i.radiusKm} km`,
          ].join(" · ")
        : "Manual volume";
  return [
    i.channel === "offline" ? "In-person" : "Online",
    i.channel === "offline" ? template : "Adyen Online",
    i.mcc ? (MCC_NAMES[i.mcc] ?? i.industryGroup) : i.industryGroup,
    scope,
    i.source === "area" && i.partnerName ? i.partnerName : null,
    // The cohort filters say which merchants the averages are of, so a saved
    // row carries them as the live scope chips do — two rows that differ only
    // in who was counted have to say so.
    i.source === "area" && i.cohort === "direct" ? "Direct merchants" : null,
    i.source === "area" && i.cohort === "partners" && !i.partnerName
      ? "Partner merchants"
      : null,
    i.source === "area" && (i.minMonthlyVolume ?? 0) > 0
      ? `${eur(i.minMonthlyVolume)}+ / month`
      : null,
    i.source === "manual"
      ? null
      : i.selMonth
        ? monthLong(i.selMonth)
        : `${monthLong(period.start)} – ${monthLong(period.end)}`,
    i.netBasis === "after" ? "After markup" : null,
  ].filter(Boolean) as string[];
}

// A name to open the rename field on, so saving a second template against the
// same merchant doesn't produce two rows called the same thing.
export function suggestScenarioName(i: ScenarioInputs): string {
  const template =
    TEMPLATE_OPTIONS.find((t) => t.key === i.template)?.label ?? i.template;
  const scope =
    i.source === "business"
      ? (i.selectedBiz?.displayName ?? "Business")
      : i.source === "area"
        ? (i.areaCentre === "business"
            ? i.areaBizCity
            : (i.areaAddress?.label ?? i.city)
          ).split(",")[0]
        : "Manual";
  return `${i.channel === "offline" ? template : "Online"} · ${scope}`;
}

// What a scenario's net take came to as a share of its volume. Derived rather
// than stored beside the net it is a share of: two fields that have to agree
// are two fields that can disagree.
export function scenarioEffectiveRate(m: ScenarioMetrics): number {
  return m.tpv > 0 ? (m.monthlyNet / m.tpv) * 100 : 0;
}

export function savedAtLabel(savedAt: string): string {
  return shortDate(savedAt.slice(0, 10));
}

// ── Persistence ──

// Kept apart from the filter blob on purpose: a scenario is something someone
// asked to keep, so a version bump that invalidates a filter — or the Clear
// button, which is there to throw the filters' own leftovers away — must not
// take the saved work with it.
export const SCENARIOS_STORAGE_KEY = "pricingTool:scenarios:v1";

export function loadSavedScenarios(raw: string | null): SavedScenario[] {
  try {
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Per entry rather than all-or-nothing: one scenario written by a build
    // that has since changed shape is no reason to drop the rest.
    return parsed.filter(isSavedScenario).slice(0, MAX_SAVED_SCENARIOS);
  } catch {
    return [];
  }
}

export function readSavedScenarios(): SavedScenario[] {
  try {
    return loadSavedScenarios(
      window.localStorage.getItem(SCENARIOS_STORAGE_KEY)
    );
  } catch {
    return [];
  }
}

// Reports whether the write landed, so a caller that skips repeat writes can
// tell a stored list from one that storage refused.
export function persistSavedScenarios(list: SavedScenario[]): boolean {
  try {
    window.localStorage.setItem(SCENARIOS_STORAGE_KEY, JSON.stringify(list));
    return true;
  } catch {
    return false;
  }
}

function isSavedScenario(v: unknown): v is SavedScenario {
  const s = v as SavedScenario | null;
  if (!s || typeof s !== "object") return false;
  if (typeof s.id !== "string" || typeof s.name !== "string") return false;
  if (typeof s.savedAt !== "string") return false;
  if (!s.inputs || typeof s.inputs !== "object") return false;
  if (!s.metrics || typeof s.metrics !== "object") return false;
  return (["tpv", "txns", "monthlyNet", "annualNet"] as const).every(
    (k) => typeof s.metrics[k] === "number" && Number.isFinite(s.metrics[k])
  );
}
